import React, { useRef, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import $ from "jquery";
import moment from "moment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "bootstrap/dist/css/bootstrap.min.css";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "select2";
import "select2/dist/css/select2.min.css";
import { complaintAPI, masterDataAPI, territoryMasterAPI, userAPI } from "../services/api";
import { approverDashboardAPI } from "../services/approverDashboardApi";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import DashboardExportDropdown from "../components/common/DashboardExportDropdown";
import {
  formatDateDDMMYYYY,
  exportJsonToExcel,
  exportRowsToCsv,
  exportRowsToPdf,
} from "../utils/dashboardExportUtils";
import { clearBcmServiceRequestListState } from "../utils/bcmServiceRequestListState";
import {
  getChartDateInputWidthPx,
  getDashboardDateRangePresets,
  getDashboardFinancialYearRange,
  initDashboardCardDateRangePicker,
} from "../utils/dashboardDateRangePicker";
import {
  APPROVER_KPI_WIDGET_KEY,
  createApproverWidgetLoadingState,
} from "../utils/approverDashboardWidgetConfig";
import {
  extractRegionsFromResponse,
  extractZonesFromResponse,
  formatZoneOptionLabel,
  geoFilterArraysEqual,
  isExportMarketSelected,
  mapExportZonesToOptions,
  mapRegionToOption,
  mapTerritoryToOption,
  mapZoneToOption,
  normalizeRegionRow,
  normalizeTerritoryRow,
  normalizeZoneRow,
  parseApiEnvelope,
  resolveGeoAutoSelectionIds,
  resolveRegionGeoCandidates,
  expandZoneFilterCodes,
  getCountryCodesForSelectedZones,
  resolveZoneCodeForCountry,
  findZoneOption,
} from "../utils/dashboardGeoFilterUtils";
import {
  filterGeoOptionsForDashboardUser,
  filterMarketsForDashboardUser,
  filterMasterCountriesForDashboardUser,
  getLoggedInUserFromStorage,
  getUserAssignedMarketCodes,
  getUserScopedGeoCodesForDashboard,
  isUserGeoAllScopeForDashboard,
  resolveGeoFilterSelection,
  isGeoAllSelectionToken,
  USER_DEFAULT_GEO_CODE,
} from "../utils/userTerritoryScope";
import { isDosAdminUser, isFinalCloserUserType } from "../utils/auth";
import "./Dashboard.css";

/** Avoid hydrating thousands of geo IDs into React state (DOS Admin / ALL-scope freezes the UI). */
const MAX_GEO_AUTO_SELECT = 200;

const GEO_ALL_OPTION = { id: USER_DEFAULT_GEO_CODE, text: "All" };
const GEO_DROPDOWN_LOAD_MIN_MS = 150;

const isGeoAllOptionRow = (id, text) => {
  const sid = String(id ?? "").trim().toLowerCase();
  const label = String(text ?? "").trim().toLowerCase();
  return (
    sid === "all" ||
    sid === String(USER_DEFAULT_GEO_CODE).toLowerCase() ||
    label === "all"
  );
};

const isGeoAllFilterValue = (val) =>
  Array.isArray(val) &&
  val.length > 0 &&
  val.every((id) => isGeoAllSelectionToken(id));

const selectionIncludesGeoAll = (raw, optionList) =>
  (Array.isArray(raw) ? raw : []).some((id) => {
    const sid = String(id ?? "").trim();
    if (isGeoAllSelectionToken(sid)) return true;
    const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === sid);
    return isGeoAllOptionRow(sid, opt?.text);
  });

// Plugin to display values on pie chart segments
const pieChartValuePlugin = {
  id: "pieChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    // Only apply to pie/doughnut charts
    if (chart.config.type !== "pie" && chart.config.type !== "doughnut") {
      return;
    }

    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((element, index) => {
      const value = data[index];
      const position = element.tooltipPosition();

      ctx.save();
      ctx.fillStyle = "#333";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Display value
      ctx.fillText(value, position.x, position.y);
      ctx.restore();
    });
  },
};

// Plugin to display values on bar chart - same style as pie chart
const barChartValuePlugin = {
  id: "barChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    // Only apply to bar charts
    if (chart.config.type !== "bar") {
      return;
    }

    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((element, index) => {
      const value = data[index];
      if (value === null || value === undefined) return;

      const { x, y } = element.tooltipPosition();
      const barHeight = element.height;
      const barY = y - barHeight;

      ctx.save();
      ctx.fillStyle = "#333";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Display value - center of the bar (same style as pie chart)
      const textY = barY + barHeight / 2;
      ctx.fillText(value, x, textY);
      ctx.restore();
    });
  },
};

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  pieChartValuePlugin,
  barChartValuePlugin
);

function getApproverDashboardDataPayload(response) {
  if (!response) return null;
  const ok = response.success === true || response.Success === true;
  if (!ok) return null;
  const raw = response.data !== undefined ? response.data : response.Data;
  return raw && typeof raw === "object" ? raw : null;
}

/** Same palette as /csrp/dashboard Top 10 Customers bar chart */
const DASHBOARD_BAR_COLORS = [
  "#4a90e2",
  "#50c878",
  "#ff9800",
  "#17a2b8",
  "#9c27b0",
  "#f44336",
  "#00bcd4",
  "#ffeb3b",
  "#795548",
  "#607d8b",
];

function barColorsForCount(count) {
  const n = Math.max(Number(count) || 1, 1);
  const colors = [];
  for (let i = 0; i < n; i++) {
    colors.push(DASHBOARD_BAR_COLORS[i % DASHBOARD_BAR_COLORS.length]);
  }
  return colors;
}

const ApproverDashboard = () => {
  const navigate = useNavigate();
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);
  const productVerticalChartRef = useRef(null);
  const valueSlabChartRef = useRef(null);
  const dateRangePickerRef = useRef(null);
  const filterDateRangePickerRef = useRef(null);
  const statusCardDatePickerRef = useRef(null);
  const categoryCardDatePickerRef = useRef(null);
  const bvCardDatePickerRef = useRef(null);
  const valueSlabCardDatePickerRef = useRef(null);
  const loadSrStatusChartRef = useRef(null);
  const loadSrCategoryChartRef = useRef(null);
  const loadProductVerticalChartRef = useRef(null);
  const loadValueSlabChartRef = useRef(null);
  const skipDateFilterDashboardLoadRef = useRef(true);
  const prevExportCascadeRef = useRef(null);
  const userGeographyRef = useRef(null);
  const zoneOptionsRef = useRef([]);
  const regionOptionsRef = useRef([]);
  const geoLoadGenRef = useRef({ country: 0, zone: 0, region: 0, territory: 0 });
  /** Abort in-flight dashboard SQL when leaving page / opening BCM list (frees connection pool). */
  const dashboardAbortRef = useRef(null);

  const loginUser = useMemo(() => getLoggedInUserFromStorage(), []);
  const skipHeavyGeoHydration = useMemo(
    () => isDosAdminUser(loginUser) || isDosAdminUser(),
    [loginUser]
  );
  const isFinalCloser = useMemo(
    () => isFinalCloserUserType(loginUser) || isFinalCloserUserType(),
    [loginUser]
  );

  const wrapTickLabel = (raw, maxLineLen = 14, maxLines = 3) => {
    const text = String(raw ?? "").trim();
    if (!text) return "";
    const parts = text.split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = "";
    for (const p of parts) {
      const next = cur ? `${cur} ${p}` : p;
      if (next.length <= maxLineLen) cur = next;
      else {
        if (cur) lines.push(cur);
        cur = p;
      }
      if (lines.length >= maxLines) break;
    }
    if (lines.length < maxLines && cur) lines.push(cur);
    return lines.length > 1 ? lines : lines[0];
  };

  // Refs for select2 dropdowns
  const statusSelectRef = useRef(null);
  const complaintCategorySelectRef = useRef(null);
  const bvSelectRef = useRef(null);

  // Date range filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeText, setDateRangeText] = useState("");

  const defaultFY = getDashboardFinancialYearRange();

  // Filter states (aligned with /csrp/dashboard)
  const [filters, setFilters] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
    status: [],
    complaintCategory: "",
    // Multi-select for Product Vertical (same as /csrp/dashboard)
    bv: [],
    invoiceNo: "",
    salesExecutiveName: "",
    market: "",
    country: [],
    zone: [],
    region: [],
    territory: [],
  });

  // Dropdown data states
  const [statusOptions, setStatusOptions] = useState([]);
  const [srCategoryOptions, setSrCategoryOptions] = useState([]);
  const [marketOptions, setMarketOptions] = useState([]);
  const [bvOptions, setBvOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [geoLoading, setGeoLoading] = useState({
    country: false,
    zone: false,
    region: false,
    territory: false,
  });
  const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
  const [showInvoiceSuggestions, setShowInvoiceSuggestions] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [userGeography, setUserGeography] = useState(null);

  const assignedMarketCodes = useMemo(
    () => getUserAssignedMarketCodes(loginUser),
    [loginUser]
  );
  const marketDropdownDisabled = assignedMarketCodes.length <= 1;

  useEffect(() => {
    userGeographyRef.current = userGeography;
  }, [userGeography]);

  useEffect(() => {
    zoneOptionsRef.current = zoneOptions;
  }, [zoneOptions]);

  useEffect(() => {
    regionOptionsRef.current = regionOptions;
  }, [regionOptions]);

  const loadUserGeography = async () => {
    const id = loginUser?.userIdCode || loginUser?.UserIdCode;
    if (!id) return;
    try {
      const res = await userAPI.getGeography(id);
      const payload = res?.data ?? res?.Data ?? res;
      userGeographyRef.current = payload;
      setUserGeography(payload);
    } catch (e) {
      console.error("Error loading user geography for approver dashboard filters:", e);
    }
  };

  const isExportCascade = useMemo(
    () => isExportMarketSelected(filters.market, marketOptions),
    [filters.market, marketOptions]
  );
  const isExportCascadeRef = useRef(isExportCascade);
  isExportCascadeRef.current = isExportCascade;

  const zoneOptionIdsKey = useMemo(
    () =>
      (zoneOptions || [])
        .map((o) => String(o?.id ?? "").trim())
        .filter(Boolean)
        .join("|"),
    [zoneOptions]
  );

  const exportCountryOptions = useMemo(() => {
    if (!isExportCascade || !filters.zone?.length) return [];
    if (isGeoAllFilterValue(filters.zone)) return [GEO_ALL_OPTION];
    const codes = getCountryCodesForSelectedZones(filters.zone, zoneOptions);
    if (!codes.size) return countryOptions;
    return countryOptions.filter(
      (c) => codes.has(c.id) || isGeoAllSelectionToken(c.id)
    );
  }, [isExportCascade, filters.zone, zoneOptions, countryOptions]);

  const runGeoDropdownLoad = async (level, task) => {
    geoLoadGenRef.current[level] += 1;
    const generation = geoLoadGenRef.current[level];
    const isCurrent = () => geoLoadGenRef.current[level] === generation;
    setGeoLoading((prev) => ({ ...prev, [level]: true }));
    const startedAt = Date.now();
    try {
      await task(isCurrent);
    } finally {
      const elapsed = Date.now() - startedAt;
      const remain = GEO_DROPDOWN_LOAD_MIN_MS - elapsed;
      if (remain > 0) await new Promise((r) => setTimeout(r, remain));
      if (isCurrent()) {
        setGeoLoading((prev) => ({ ...prev, [level]: false }));
      }
    }
  };

  const withGeoAllOption = (options, level) => {
    const list = (Array.isArray(options) ? options : []).filter(
      (o) => o && !isGeoAllOptionRow(o.id, o.text)
    );
    if (isUserGeoAllScopeForDashboard(loginUser, level)) {
      return [GEO_ALL_OPTION, ...list];
    }
    return list;
  };

  const joinGeoFilterForApi = (val) => {
    if (isGeoAllFilterValue(val)) return null;
    if (Array.isArray(val) && val.length) {
      const real = val.filter((id) => !isGeoAllSelectionToken(id));
      const joined = real.filter(Boolean).join(",");
      return joined || null;
    }
    if (val && String(val).trim() && !isGeoAllSelectionToken(val)) {
      return String(val).trim();
    }
    return null;
  };

  const handleGeoFilterChange = (field, value, optionList = []) => {
    const exportCascade = isExportMarketSelected(filters.market, marketOptions);
    const raw = Array.isArray(value)
      ? value.map((x) => String(x ?? "").trim()).filter(Boolean)
      : [];
    const realOnly = raw.filter((id) => {
      const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === id);
      return !isGeoAllOptionRow(id, opt?.text);
    });
    const pickedAll = selectionIncludesGeoAll(raw, optionList);
    const prevWasAll = isGeoAllFilterValue(filters[field]);

    if (pickedAll && !(prevWasAll && realOnly.length > 0)) {
      const allVal = [USER_DEFAULT_GEO_CODE];
      if (field === "country") {
        setZoneOptions([GEO_ALL_OPTION]);
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({
          ...prev,
          country: allVal,
          zone: allVal,
          region: allVal,
          territory: allVal,
        }));
        return;
      }
      if (field === "zone") {
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        if (exportCascade) {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            country: allVal,
            region: allVal,
            territory: allVal,
          }));
        } else {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            region: allVal,
            territory: allVal,
          }));
        }
        return;
      }
      if (field === "region") {
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({ ...prev, region: allVal, territory: allVal }));
        return;
      }
      if (field === "territory") {
        setFilters((prev) => ({ ...prev, territory: allVal }));
        return;
      }
    }

    const selected =
      realOnly.length > 0
        ? realOnly
        : resolveGeoFilterSelection(value, optionList).filter(
            (id) => !isGeoAllSelectionToken(id) && !isGeoAllOptionRow(id)
          );

    setFilters((prev) => {
      if (field === "country") {
        if (exportCascade) {
          return { ...prev, country: selected, region: [], territory: [] };
        }
        return { ...prev, country: selected, zone: [], region: [], territory: [] };
      }
      if (field === "zone") {
        if (exportCascade) {
          return { ...prev, zone: selected, country: [], region: [], territory: [] };
        }
        return { ...prev, zone: selected, region: [], territory: [] };
      }
      if (field === "region") {
        return { ...prev, region: selected, territory: [] };
      }
      if (field === "territory") {
        return { ...prev, territory: selected };
      }
      return prev;
    });
  };

  const pickDefaultMarket = (options) => {
    if (!Array.isArray(options) || !options.length) return "";
    if (assignedMarketCodes.length === 1) return assignedMarketCodes[0];
    const domestic =
      options.find((o) => String(o.text || "").trim().toLowerCase() === "domestic") ||
      options.find((o) => String(o.id || "").trim().toUpperCase() === "M001") ||
      options[0];
    return domestic?.id || "";
  };

  // Approver SR status data (loaded via same dashboard-data API as /csrp/dashboard)
  const [srData, setSrData] = useState({
    pendingSRCount: 0,
    approvedSRCount: 0,
    rejectedSRCount: 0,
    closedSRCount: 0,
    queryRaisedSRCount: 0,
    queryToRespondSRCount: 0,
    specialCaseSRCount: 0,
    slaBreachedSRCount: 0,
    avgApprovalTatDays: null,
  });
  const [chartLoading, setChartLoading] = useState(() => createApproverWidgetLoadingState(true));

  const totalSRCount =
    srData.pendingSRCount +
    srData.approvedSRCount +
    srData.rejectedSRCount +
    srData.closedSRCount +
    srData.queryRaisedSRCount;

  // SR category chart data for approver (dynamic; from dashboard-data API)
  const [srCategoryData, setSrCategoryData] = useState({
    labels: [],
    srCounts: [],
  });

  // Product vertical data (from API)
  const [productVerticalData, setProductVerticalData] = useState({
    codes: [],
    verticals: [],
    srCounts: [],
  });

  // Value slab data (from API)
  const [valueSlabData, setValueSlabData] = useState({
    slabs: [],
    srCounts: [],
  });

  const [srStatusChartData, setSrStatusChartData] = useState({
    pendingSRCount: 0,
    approvedSRCount: 0,
    rejectedSRCount: 0,
    closedSRCount: 0,
    queryRaisedSRCount: 0,
  });
  const [statusCardDate, setStatusCardDate] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
  });
  const [categoryCardDate, setCategoryCardDate] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
  });
  const [bvCardDate, setBvCardDate] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
  });
  const [valueSlabCardDate, setValueSlabCardDate] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
  });

  const chartCardDatePickerRefs = [
    statusCardDatePickerRef,
    categoryCardDatePickerRef,
    bvCardDatePickerRef,
    valueSlabCardDatePickerRef,
  ];

  // Build BCM drill-down query params while preserving dashboard filter context.
  const buildBcmDrilldownSearchParams = React.useCallback(
    ({ statusToken, queryRaised, queryToRespond, specialCase, slaBreached, valueSlab, bvCode, bvName, category, fromDate, toDate } = {}) => {
      const qp = new URLSearchParams();

      const rangeStart = fromDate ?? filters?.dateRangeStart;
      const rangeEnd = toDate ?? filters?.dateRangeEnd;
      if (rangeStart) qp.set("fromDate", rangeStart);
      if (rangeEnd) qp.set("toDate", rangeEnd);

      // Global filters: Business Vertical (CATGCODE CSV)
      // Skip for KPI card drill-downs (Pending/Approved/Closed/SLA/Queries) — list SP
      // pending path + client BV filter can disagree with dashboard and show 0 rows.
      const selectedBvIds = Array.isArray(filters?.bv)
        ? filters.bv.map((v) => String(v ?? "").trim()).filter(Boolean)
        : [];
      const hasStatusToken = statusToken != null && String(statusToken).trim() !== "";
      const hasCardDrilldown =
        hasStatusToken ||
        queryRaised === true ||
        queryToRespond === true ||
        specialCase === true ||
        slaBreached === true;
      if (selectedBvIds.length > 0 && !hasCardDrilldown && bvCode == null) {
        qp.set("bv", selectedBvIds.join(","));
      }

      // Global filters: SR Category — same: only when not a KPI card/pie click
      const catId = filters?.complaintCategory != null ? String(filters.complaintCategory).trim() : "";
      if (catId && !hasCardDrilldown && category == null) {
        const opt = Array.isArray(srCategoryOptions)
          ? srCategoryOptions.find((o) => String(o?.id ?? "").trim() === catId)
          : null;
        const catText = opt?.text != null ? String(opt.text).trim() : catId;
        if (catText) qp.set("category", catText);
      }

      // Dashboard status multi-select — do not intersect with KPI card clicks
      const selectedStatusIds = Array.isArray(filters?.status)
        ? filters.status.map((v) => String(v ?? "").trim()).filter(Boolean)
        : [];
      if (selectedStatusIds.length > 0 && !hasCardDrilldown) {
        qp.set("statusSelected", selectedStatusIds.join(","));
      }

      // Drill-down context (BV / value slab / category): list SP uses @ChartDrilldownActive when status omitted.
      if (hasStatusToken) qp.set("status", String(statusToken).trim());
      if (queryRaised === true) qp.set("queryRaised", "true");
      if (queryToRespond === true) qp.set("queryToRespond", "true");
      if (specialCase === true) qp.set("specialCase", "true");
      if (slaBreached === true) qp.set("slaBreached", "true");
      if (valueSlab != null && String(valueSlab).trim()) qp.set("valueSlab", String(valueSlab).trim());
      if (bvCode != null && String(bvCode).trim()) qp.set("bv", String(bvCode).trim()); // specific bar click overrides dashboard BV
      if (bvName != null && String(bvName).trim()) qp.set("bvName", String(bvName).trim());
      if (category != null && String(category).trim()) qp.set("category", String(category).trim()); // bar click overrides dashboard SR Category

      return qp;
    },
    [filters, srCategoryOptions]
  );

  const navigateToBcmList = React.useCallback(
    (opts = {}) => {
      // Stop dashboard SP so View SR list is not blocked waiting for a pool connection.
      try {
        dashboardAbortRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
      clearBcmServiceRequestListState();
      const qp = buildBcmDrilldownSearchParams(opts);
      if (!qp.toString()) {
        navigate("/bcm-service-requests");
        return;
      }
      // One navigation only. A follow-up ENC replace remounted the list (lost state while
      // decrypting), started overlapping SP calls, and hit the 20s loading timeout.
      // Plain query + location.state is enough for first paint; refresh still works with plain params.
      const bcmDrilldownPlain = Object.fromEntries(qp.entries());
      navigate(
        { pathname: "/bcm-service-requests", search: `?${qp.toString()}` },
        { state: { bcmDrilldownPlain } }
      );
    },
    [buildBcmDrilldownSearchParams, navigate]
  );

  // Chart data configuration
  const chartData = {
    labels: [
      "Pending",
      "Approved",
      "Rejected",
      "Closed",
      "Query raised",
    ],
    datasets: [
      {
        label: "SR Count",
        data: [
          srStatusChartData.pendingSRCount,
          srStatusChartData.approvedSRCount,
          srStatusChartData.rejectedSRCount,
          srStatusChartData.closedSRCount,
          srStatusChartData.queryRaisedSRCount,
        ],
        backgroundColor: [
          "#ff9800", // Orange - Pending
          "#50c878", // Green - Approved
          "#dc3545", // Red - Rejected
          "#4a90e2", // Blue - Closed
          "#9c27b0", // Purple - Query raised
        ],
        borderColor: ["#ff9800", "#50c878", "#dc3545", "#4a90e2", "#9c27b0"],
        borderWidth: 1,
      },
    ],
  };

  // Handle pie chart segment click
  const handlePieChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;

      // Map segment index to status ID
      // 0: Pending -> status 2 (Pending Approval)
      // 1: Approved -> status 3 (Approved)
      // 2: Rejected -> status 4 (Rejected)
      // 3: Closed -> status 5 (Closed)
      // 4: Query raised -> special handling (query parameter)
      const statusMap = {
        0: 2, // Pending
        1: 3, // Approved
        2: 4, // Rejected
        3: 5, // Closed
      };

      const statusId = statusMap[clickedElementIndex];
      const chartDates = {
        fromDate: statusCardDate.dateRangeStart,
        toDate: statusCardDate.dateRangeEnd,
      };
      if (statusId) {
        navigateToBcmList({ statusToken: statusId, ...chartDates });
      } else if (clickedElementIndex === 4) {
        navigateToBcmList({ queryRaised: true, ...chartDates });
      }
    }
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false, // Hide default legend as we'll use custom one
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "60%", // Makes it a donut chart
    onClick: handlePieChartClick, // Add click handler
  };

  // Bar chart data configuration (SRs by SR Category) - mirror /csrp/dashboard
  const barChartData = {
    labels: srCategoryData.labels,
    datasets: [
      {
        label: "SR Count",
        data: srCategoryData.srCounts,
        backgroundColor: [
          "#4a90e2", // Blue
          "#50c878", // Green
          "#ff9800", // Orange
          "#17a2b8", // Light blue/cyan
        ],
        borderColor: ["#4a90e2", "#50c878", "#ff9800", "#17a2b8"],
        borderWidth: 1,
        // Match bar thickness with other bar charts on this page
        barPercentage: 0.7,
        categoryPercentage: srCategoryData.labels.length <= 1 ? 0.3 : 0.8,
        barThickness: 40,
      },
    ],
  };

  // Handle bar chart click — match dashboard chart totals (pending + done by this approver)
  const handleBarChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;
      const categoryName = barChartData.labels[clickedElementIndex];
      if (categoryName) {
        navigateToBcmList({
          category: String(categoryName),
          fromDate: categoryCardDate.dateRangeStart,
          toDate: categoryCardDate.dateRangeEnd,
        });
      }
    }
  };

  // Dynamic Y-axis max for SR Category chart (avoid fixed 500; scale to data)
  const categoryDataValues = srCategoryData.srCounts || [];
  const categoryMaxVal = Math.max(0, ...categoryDataValues);
  const categoryYMax =
    categoryMaxVal === 0
      ? 5
      : Math.ceil(categoryMaxVal * 1.2) <= 10
        ? Math.ceil(categoryMaxVal * 1.2)
        : Math.ceil((categoryMaxVal * 1.2) / 5) * 5;
  const categoryStep = categoryYMax <= 10 ? 1 : categoryYMax <= 50 ? 5 : 10;

  // Bar chart options (SRs by SR Category)
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false, // Hide legend
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `Count: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: categoryYMax,
        suggestedMax: categoryYMax,
        ticks: {
          stepSize: categoryStep,
          precision: 0,
          maxTicksLimit: 11,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: { size: 11, weight: "600" },
          crossAlign: "near",
          callback: function (value, index) {
            const label = srCategoryData.labels?.[index] ?? value;
            return wrapTickLabel(label, 14, 2);
          },
        },
        grid: {
          display: false,
        },
      },
    },
    onClick: handleBarChartClick, // Add click handler
  };

  // Value slab bar chart data configuration (SRs by Value Slab)
  const valueSlabChartData = {
    labels: valueSlabData.slabs,
    datasets: [
      {
        label: "SR Count",
        data: valueSlabData.srCounts,
        backgroundColor: barColorsForCount(valueSlabData.slabs.length),
        borderColor: barColorsForCount(valueSlabData.slabs.length),
        borderWidth: 1,
        barPercentage: 0.7,
        categoryPercentage:
          valueSlabData.slabs.length <= 1 ? 0.3 : 0.8,
        barThickness: 40,
      },
    ],
  };

  // Handle value slab bar chart click
  const handleValueSlabChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;

      // Get the value slab from the clicked bar
      const valueSlab = valueSlabChartData.labels[clickedElementIndex];
      if (valueSlab) {
        navigateToBcmList({
          valueSlab,
          fromDate: valueSlabCardDate.dateRangeStart,
          toDate: valueSlabCardDate.dateRangeEnd,
        });
      }
    }
  };

  // Dynamic Y-axis for Value Slab chart (scale to data)
  const valueSlabMaxVal =
    valueSlabData.srCounts.length > 0
      ? Math.max(...valueSlabData.srCounts)
      : 0;
  const valueSlabYMax =
    valueSlabMaxVal === 0
      ? 5
      : valueSlabMaxVal <= 10
        ? Math.ceil(valueSlabMaxVal * 1.2)
        : Math.ceil((valueSlabMaxVal * 1.2) / 5) * 5;

  // Value slab bar chart options
  const valueSlabChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false, // Hide legend
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `SR Count: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: valueSlabYMax,
        suggestedMax: valueSlabYMax,
        ticks: {
          precision: 0,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: { size: 11, weight: "600" },
          crossAlign: "near",
          callback: function (value, index) {
            const label = valueSlabData.slabs?.[index] ?? value;
            return wrapTickLabel(label, 14, 2);
          },
        },
        grid: {
          display: false,
        },
      },
    },
    onClick: handleValueSlabChartClick, // Add click handler
  };

  // Product vertical bar chart data configuration (SRs by Product Vertical)
  const productVerticalChartData = useMemo(() => {
    const barColors = barColorsForCount(productVerticalData.verticals.length);
    return {
      labels: productVerticalData.verticals,
      datasets: [
        {
          label: "SR Count",
          data: productVerticalData.srCounts,
          backgroundColor: barColors,
          borderColor: barColors,
          borderWidth: 1,
          barPercentage: 0.7,
          categoryPercentage:
            productVerticalData.verticals.length <= 1 ? 0.3 : 0.8,
          barThickness: 40,
        },
      ],
    };
  }, [productVerticalData.verticals, productVerticalData.srCounts]);

  // Resolve CATGCODE for drill-down — exact bar only (do not expand same-name codes).
  // Expanding all master rows with the same name made chart=2 but list=4.
  const resolveBusinessVerticalDrilldown = React.useCallback(
    (verticalName, codeFromApi) => {
      const fromApi = codeFromApi != null ? String(codeFromApi).trim() : "";
      const bvName = String(verticalName ?? "").trim();
      const bvCode = fromApi || bvName;
      return { bvCode, bvName };
    },
    []
  );

  // Handle product vertical bar chart click
  const handleProductVerticalChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;
      const verticalName = productVerticalData.verticals?.[clickedElementIndex] ?? "";
      const codeFromApi = Array.isArray(productVerticalData.codes)
        ? productVerticalData.codes[clickedElementIndex]
        : null;
      const { bvCode, bvName } = resolveBusinessVerticalDrilldown(verticalName, codeFromApi);

      if (bvCode) {
        navigateToBcmList({
          bvCode,
          bvName,
          fromDate: bvCardDate.dateRangeStart,
          toDate: bvCardDate.dateRangeEnd,
        });
      }
    }
  };

  // Dynamic Y-axis for Product Vertical chart (scale to data)
  const pvMaxVal =
    productVerticalData.srCounts.length > 0
      ? Math.max(...productVerticalData.srCounts)
      : 0;
  const pvYMax =
    pvMaxVal === 0
      ? 5
      : pvMaxVal <= 10
        ? Math.ceil(pvMaxVal * 1.2)
        : Math.ceil((pvMaxVal * 1.2) / 5) * 5;

  // Product vertical bar chart options
  const productVerticalChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false, // Hide legend
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `SR Count: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: pvYMax,
        suggestedMax: pvYMax,
        ticks: {
          precision: 0,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: { size: 11, weight: "600" },
          crossAlign: "near",
          callback: function (value, index) {
            const label = productVerticalData.verticals?.[index] ?? value;
            return wrapTickLabel(label, 14, 2);
          },
        },
        grid: {
          display: false,
        },
      },
    },
    onClick: handleProductVerticalChartClick, // Add click handler
  };

  // Load Status options from SRSTATUSMASTER (same as /csrp/dashboard)
  const loadStatusOptions = async () => {
    try {
      const response = await approverDashboardAPI.getSRStatuses(true);
      const ok = response?.success === true || response?.Success === true;
      const list = response?.data ?? response?.Data;
      if (ok && Array.isArray(list)) {
        const options = list.map((s) => ({
          // Backend uses System.Text.Json camelCase, which converts `SRStatusCode` -> `sRStatusCode`
          id: s.srStatusCode || s.sRStatusCode || s.SRStatusCode || "",
          text: s.name || s.Name || "",
        }));
        setStatusOptions(options);
      }
    } catch (e) {
      console.error("Error loading status options:", e);
    }
  };

  // Load SR Category options from SRTYPES
  const loadSrCategoryOptions = async () => {
    try {
      const response = await approverDashboardAPI.getSrCategories();
      const ok = response?.success === true || response?.Success === true;
      const list = response?.data ?? response?.Data;
      if (ok && Array.isArray(list)) {
        const options = list.map((x) => ({
          id: x.value || x.Value,
          text: x.text || x.Text || x.value || x.Value,
        }));
        setSrCategoryOptions(options);
      }
    } catch (e) {
      console.error("Error loading SR category options:", e);
    }
  };

  // Load Product Vertical options from CATEGORYMASTER (scoped to selected market)
  const loadBVOptions = async (marketCode) => {
    try {
      const response = await approverDashboardAPI.getProductVerticalOptions({
        isActive: true,
        marketCode: marketCode || undefined,
        pageNumber: 1,
        pageSize: 500,
      });
      if (response?.success && response.data?.categories) {
        const list = response.data.categories || response.data.Categories || [];
        const options = list.map((c) => ({
          id: c.catgCode || c.CatgCode,
          text: c.catgName || c.CatgName || c.catgCode || c.CatgCode,
        }));
        setBvOptions(options);
      }
    } catch (e) {
      console.error("Error loading BV options:", e);
    }
  };

  const loadMarketOptions = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      const { ok, payload } = parseApiEnvelope(response);
      const markets = payload?.markets || payload?.Markets || payload || [];
      const allOptions = (Array.isArray(markets) ? markets : [])
        .filter((m) => m.isActive !== false && m.IsActive !== false)
        .map((m) => ({
          id: m.marketCode || m.MARKETCODE || m.MarketCode,
          text: m.marketName || m.MARKETNAME || m.MarketName || m.marketCode,
        }))
        .filter((o) => o.id);
      const options = filterMarketsForDashboardUser(allOptions, loginUser);
      setMarketOptions(options);

      let nextMarket = "";
      setFilters((prev) => {
        nextMarket = prev.market || pickDefaultMarket(options);
        if (prev.market === nextMarket) return prev;
        return { ...prev, market: nextMarket };
      });
      if (!nextMarket) nextMarket = pickDefaultMarket(options);
      return nextMarket;
    } catch (e) {
      console.error("Error loading Market options:", e);
      return "";
    }
  };

  // Load Country options — same cascade as /csrp/reports/domestic (scoped to logged-in user)
  const loadCountryOptions = async (marketCodeOverride) => {
    await runGeoDropdownLoad("country", async (isCurrent) => {
      try {
        const response = await masterDataAPI.getCountries();
        if (!isCurrent()) return;
        const { ok, payload } = parseApiEnvelope(response);
        const raw = ok && Array.isArray(payload) ? payload : [];
        const scoped = filterMasterCountriesForDashboardUser(
          raw,
          loginUser,
          userGeographyRef.current
        );
        const options = withGeoAllOption(scoped, "country");
        if (!isCurrent()) return;
        setCountryOptions(options);

        setFilters((prev) => {
          const market = marketCodeOverride || prev.market;
          if (!market) return prev;
          const exportMode = isExportMarketSelected(market, marketOptions);
          if (exportMode) return prev;
          if (isUserGeoAllScopeForDashboard(loginUser, "country")) {
            const allVal = [USER_DEFAULT_GEO_CODE];
            if (geoFilterArraysEqual(prev.country, allVal)) return prev;
            return {
              ...prev,
              country: allVal,
              zone: allVal,
              region: allVal,
              territory: allVal,
            };
          }
          const autoIds = resolveGeoAutoSelectionIds({
            scopedOptions: options,
            user: loginUser,
            level: "country",
            isExportCascade: false,
            parentReady: true,
          });
          if (
            !skipHeavyGeoHydration &&
            autoIds &&
            autoIds.length <= MAX_GEO_AUTO_SELECT &&
            !geoFilterArraysEqual(prev.country, autoIds)
          ) {
            return { ...prev, country: autoIds, zone: [], region: [], territory: [] };
          }
          return prev;
        });
      } catch (e) {
        console.error("Error loading Country options:", e);
        if (!isCurrent()) return;
        setCountryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadZoneOptions = async (countryCodes) => {
    await runGeoDropdownLoad("zone", async (isCurrent) => {
      try {
        const merged = [];
        const codes = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!codes.length) {
          const res = await territoryMasterAPI.getZones();
          if (!isCurrent()) return;
          const raw = extractZonesFromResponse(res);
          const active = Array.isArray(raw)
            ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
            : [];
          active.forEach((z) =>
            merged.push(normalizeZoneRow(z, z.countryCode || z.CountryCode))
          );
        } else {
          const zoneResults = await Promise.all(
            codes.map(async (cc) => {
              const res = await territoryMasterAPI.getZones(cc);
              const raw = extractZonesFromResponse(res);
              const active = Array.isArray(raw)
                ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
                : [];
              return active.map((z) => normalizeZoneRow(z, cc));
            })
          );
          if (!isCurrent()) return;
          zoneResults.flat().forEach((z) => merged.push(z));
        }
        const activeRows = merged.filter(
          (z) => z.zoneCode && !isGeoAllOptionRow(z.zoneCode, z.zoneName)
        );
        const exportMode = isExportCascadeRef.current;
        let mapped = exportMode
          ? mapExportZonesToOptions(activeRows, countryOptions)
          : (() => {
              const byZone = new Map();
              activeRows.forEach((z) => {
                byZone.set(z.zoneCode, mapZoneToOption(z, countryOptions));
              });
              return [...byZone.values()];
            })();
        mapped = filterGeoOptionsForDashboardUser(
          mapped,
          loginUser,
          "zone",
          userGeographyRef.current
        );
        if (!isCurrent()) return;
        const options = withGeoAllOption(mapped, "zone");
        setZoneOptions(options);

        setFilters((prev) => {
          if (!prev.market) return prev;
          const exportModeAuto = isExportMarketSelected(prev.market, marketOptions);
          if (isUserGeoAllScopeForDashboard(loginUser, "zone")) {
            const allVal = [USER_DEFAULT_GEO_CODE];
            if (geoFilterArraysEqual(prev.zone, allVal)) return prev;
            return { ...prev, zone: allVal, region: allVal, territory: allVal };
          }
          const autoIds = resolveGeoAutoSelectionIds({
            scopedOptions: options,
            user: loginUser,
            level: "zone",
            isExportCascade: exportModeAuto,
            parentReady: exportModeAuto || !!prev.country?.length,
          });
          if (
            !skipHeavyGeoHydration &&
            autoIds &&
            autoIds.length <= MAX_GEO_AUTO_SELECT &&
            !geoFilterArraysEqual(prev.zone, autoIds)
          ) {
            return { ...prev, zone: autoIds, region: [], territory: [] };
          }
          return prev;
        });
      } catch (e) {
        console.error("Error loading Zone options:", e);
        if (!isCurrent()) return;
        setZoneOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadRegionOptions = async (zoneCodes, countryCodes, zonesList, exportMode) => {
    await runGeoDropdownLoad("region", async (isCurrent) => {
      try {
        const zSel = Array.isArray(zoneCodes)
          ? zoneCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        const cSel = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!zSel.length || (exportMode && !cSel.length)) {
          if (!isCurrent()) return;
          regionOptionsRef.current = [GEO_ALL_OPTION];
          setRegionOptions([GEO_ALL_OPTION]);
          return;
        }
        const regionJobs = [];
        for (const zoneOptionId of zSel) {
          const zrow = findZoneOption(zonesList, zoneOptionId);
          const linkedCountries = Array.isArray(zrow?.countryCodes)
            ? zrow.countryCodes.map((c) => String(c).trim()).filter(Boolean)
            : zrow?.countryCode
              ? [String(zrow.countryCode).trim()]
              : [];
          // Domestic: prefer selected filter countries (same as reports hook)
          const countriesToQuery = exportMode
            ? cSel.filter(
                (cc) =>
                  !linkedCountries.length || linkedCountries.includes(String(cc).trim())
              )
            : cSel.length
              ? cSel.filter(
                  (cc) =>
                    !linkedCountries.length ||
                    linkedCountries.includes(String(cc).trim())
                )
              : linkedCountries.length
                ? linkedCountries
                : [];
          for (const cc of countriesToQuery) {
            if (!cc || isGeoAllSelectionToken(cc)) continue;
            const zoneCode =
              resolveZoneCodeForCountry(zoneOptionId, cc, zonesList) || zoneOptionId;
            if (!zoneCode) continue;
            regionJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getRegions(cc, zoneCode);
                const raw = extractRegionsFromResponse(res);
                const active = Array.isArray(raw)
                  ? raw.filter((r) => r.isActive !== false && r.IsActive !== false)
                  : [];
                return active;
              })()
            );
          }
        }
        const regionResults = await Promise.all(regionJobs);
        if (!isCurrent()) return;
        const merged = [];
        regionResults.forEach((active) => {
          active.forEach((r) => merged.push(normalizeRegionRow(r)));
        });
        const byReg = new Map();
        merged.forEach((r) => {
          if (!r.regionCode || isGeoAllOptionRow(r.regionCode, r.regionName)) return;
          byReg.set(r.regionCode, mapRegionToOption(r));
        });
        const scoped = filterGeoOptionsForDashboardUser(
          [...byReg.values()],
          loginUser,
          "region",
          userGeographyRef.current
        );
        if (!isCurrent()) return;
        const options = withGeoAllOption(scoped, "region");
        regionOptionsRef.current = options;
        setRegionOptions(options);

        setFilters((prev) => {
          if (!prev.market) return prev;
          const exportModeAuto = isExportMarketSelected(prev.market, marketOptions);
          if (isUserGeoAllScopeForDashboard(loginUser, "region")) {
            const allVal = [USER_DEFAULT_GEO_CODE];
            if (geoFilterArraysEqual(prev.region, allVal)) return prev;
            return { ...prev, region: allVal, territory: allVal };
          }
          const parentReady = exportModeAuto
            ? !!prev.zone?.length && !!prev.country?.length
            : !!prev.zone?.length;
          const autoIds = resolveGeoAutoSelectionIds({
            scopedOptions: options,
            user: loginUser,
            level: "region",
            isExportCascade: exportModeAuto,
            parentReady,
          });
          if (
            !skipHeavyGeoHydration &&
            autoIds &&
            autoIds.length <= MAX_GEO_AUTO_SELECT &&
            !geoFilterArraysEqual(prev.region, autoIds)
          ) {
            return { ...prev, region: autoIds, territory: [] };
          }
          return prev;
        });
      } catch (e) {
        console.error("Error loading Region options:", e);
        if (!isCurrent()) return;
        regionOptionsRef.current = [GEO_ALL_OPTION];
        setRegionOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadTerritoryOptions = async (
    regionCodes,
    zonesList,
    regionsList,
    filterZoneCodes,
    filterCountryCodes
  ) => {
    await runGeoDropdownLoad("territory", async (isCurrent) => {
      try {
        // DOS Admin / ALL-territory: empty territory filter = all data. Do not download 5k rows.
        if (skipHeavyGeoHydration) {
          if (!isCurrent()) return;
          setTerritoryOptions([GEO_ALL_OPTION]);
          setFilters((prev) => {
            const allVal = [USER_DEFAULT_GEO_CODE];
            if (geoFilterArraysEqual(prev.territory, allVal)) return prev;
            return { ...prev, territory: allVal };
          });
          return;
        }

        const rSel = Array.isArray(regionCodes)
          ? regionCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!rSel.length) {
          if (!isCurrent()) return;
          setTerritoryOptions([GEO_ALL_OPTION]);
          return;
        }

        const regionsListResolved =
          (Array.isArray(regionsList) && regionsList.length
            ? regionsList
            : regionOptionsRef.current) || [];
        const zonesListResolved =
          (Array.isArray(zonesList) && zonesList.length
            ? zonesList
            : zoneOptionsRef.current) || [];

        const territoryJobs = [];
        for (const rc of rSel) {
          const row = regionsListResolved.find((x) => x.id === rc);
          const pairs = resolveRegionGeoCandidates(
            rc,
            row,
            regionsListResolved,
            zonesListResolved,
            filterZoneCodes,
            filterCountryCodes
          );
          if (!pairs.length) continue;

          for (const { cc, zc } of pairs) {
            if (isGeoAllSelectionToken(cc) || isGeoAllSelectionToken(zc)) continue;
            territoryJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getPaged({
                  countryCode: cc,
                  zoneCode: zc,
                  regionCode: rc,
                  status: "active",
                  pageNumber: 1,
                  pageSize: 5000,
                });
                const { ok, payload } = parseApiEnvelope(res);
                const items =
                  ok && payload
                    ? payload.territories || payload.Territories || []
                    : [];
                return Array.isArray(items)
                  ? items.filter((t) => t.isActive !== false && t.IsActive !== false)
                  : [];
              })()
            );
          }
        }
        const territoryResults = await Promise.all(territoryJobs);
        if (!isCurrent()) return;
        const merged = [];
        territoryResults.forEach((active) => {
          active.forEach((t) => merged.push(normalizeTerritoryRow(t)));
        });
        const byT = new Map();
        merged.forEach((t) => {
          if (!t.territoryCode || isGeoAllOptionRow(t.territoryCode, t.territoryName)) {
            return;
          }
          byT.set(t.territoryCode, mapTerritoryToOption(t));
        });
        const scoped = filterGeoOptionsForDashboardUser(
          [...byT.values()],
          loginUser,
          "territory",
          userGeographyRef.current
        );
        if (!isCurrent()) return;
        const options = withGeoAllOption(scoped, "territory");
        setTerritoryOptions(options);

        setFilters((prev) => {
          if (!prev.market || !prev.region?.length) return prev;
          if (isUserGeoAllScopeForDashboard(loginUser, "territory")) {
            const allVal = [USER_DEFAULT_GEO_CODE];
            if (geoFilterArraysEqual(prev.territory, allVal)) return prev;
            return { ...prev, territory: allVal };
          }
          const exportMode = isExportMarketSelected(prev.market, marketOptions);
          const autoIds = resolveGeoAutoSelectionIds({
            scopedOptions: options,
            user: loginUser,
            level: "territory",
            isExportCascade: exportMode,
            parentReady: true,
          });
          if (
            !skipHeavyGeoHydration &&
            autoIds &&
            autoIds.length <= MAX_GEO_AUTO_SELECT &&
            !geoFilterArraysEqual(prev.territory, autoIds)
          ) {
            return { ...prev, territory: autoIds };
          }
          return prev;
        });
      } catch (e) {
        console.error("Error loading Territory options:", e);
        if (!isCurrent()) return;
        setTerritoryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  // Load invoice suggestions (dynamic)
  const loadInvoiceSuggestions = async (searchText) => {
    if (searchText.length < 4) {
      setInvoiceSuggestions([]);
      setShowInvoiceSuggestions(false);
      return;
    }
    try {
      const resp = await complaintAPI.getServiceRequestList({
        fromDate: filters.dateRangeStart || undefined,
        toDate: filters.dateRangeEnd || undefined,
        searchText,
        pageNumber: 1,
        pageSize: 20,
      });

      const items = resp?.success ? resp?.data?.items : [];
      const unique = new Set();
      (items || []).forEach((x) => {
        const inv =
          x?.invoiceNumber ||
          x?.InvoiceNumber ||
          x?.billingInvoiceNo ||
          x?.BillingInvoiceNo ||
          "";
        const v = String(inv || "").trim();
        if (!v) return;
        if (v.startsWith(searchText)) unique.add(v);
      });

      setInvoiceSuggestions(Array.from(unique).slice(0, 10));
      setShowInvoiceSuggestions(unique.size > 0);
    } catch (e) {
      console.error("Error loading invoice suggestions:", e);
      setInvoiceSuggestions([]);
      setShowInvoiceSuggestions(false);
    }
  };

  // Handle invoice input change
  const handleInvoiceInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits
    setFilters((prev) => ({ ...prev, invoiceNo: value }));
    if (value.length >= 4) {
      loadInvoiceSuggestions(value);
    } else {
      setShowInvoiceSuggestions(false);
    }
  };

  // Handle invoice suggestion click
  const handleInvoiceSuggestionClick = (suggestion) => {
    setFilters((prev) => ({ ...prev, invoiceNo: suggestion }));
    setShowInvoiceSuggestions(false);
  };

  // Close invoice suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showInvoiceSuggestions &&
        !event.target.closest(".invoice-suggestions-list") &&
        !event.target.closest('input[placeholder="Enter invoice number"]')
      ) {
        setShowInvoiceSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInvoiceSuggestions]);

  const handleFilter = () => {
    const geoOverride = {
      complaintCategory: filters.complaintCategory,
      country: filters.country,
      zone: filters.zone,
      region: filters.region,
      territory: filters.territory,
      fromDate: filters.dateRangeStart,
      toDate: filters.dateRangeEnd,
    };
    syncChartDateFiltersFromMaster();
    loadAllDashboardData(geoOverride);
  };

  const handleReset = () => {
    const fy = getDashboardFinancialYearRange();
    const defaultMarket = pickDefaultMarket(marketOptions);
    setFilters({
      dateRange: fy.dateRange,
      dateRangeStart: fy.dateRangeStart,
      dateRangeEnd: fy.dateRangeEnd,
      status: [],
      complaintCategory: "",
      bv: [],
      invoiceNo: "",
      salesExecutiveName: "",
      market: defaultMarket,
      country: [],
      zone: [],
      region: [],
      territory: [],
    });

    if (filterDateRangePickerRef.current) {
      $(filterDateRangePickerRef.current).val(fy.dateRange);
    }

    if (statusSelectRef.current) {
      $(statusSelectRef.current).val(null).trigger("change");
    }

    if (complaintCategorySelectRef.current) {
      $(complaintCategorySelectRef.current).val(null).trigger("change");
    }

    if (bvSelectRef.current) {
      $(bvSelectRef.current).val(null).trigger("change");
    }

    setShowInvoiceSuggestions(false);
    setInvoiceSuggestions([]);
    syncChartDateFiltersFromMaster({
      dateRange: fy.dateRange,
      dateRangeStart: fy.dateRangeStart,
      dateRangeEnd: fy.dateRangeEnd,
    });

    const resetOverride = {
      fromDate: fy.dateRangeStart,
      toDate: fy.dateRangeEnd,
      status: [],
      bv: [],
      country: [],
      zone: [],
      region: [],
      territory: [],
      invoiceNo: "",
      salesExecutiveName: "",
      complaintCategory: "",
    };
    loadAllDashboardData(resetOverride);
  };

  // Load initial data
  useEffect(() => {
    const init = async () => {
      await loadUserGeography();
      loadStatusOptions();
      loadSrCategoryOptions();
      const market = await loadMarketOptions();
      if (market) {
        await loadCountryOptions(market);
        if (isExportMarketSelected(market, marketOptions)) {
          loadZoneOptions();
        }
      } else {
        loadCountryOptions();
      }
      loadAllDashboardData();
    };
    init();
    return () => {
      try {
        dashboardAbortRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cleanups = [
      initDashboardCardDateRangePicker(statusCardDatePickerRef.current, {
        initialDisplay: statusCardDate.dateRange,
        onApply: ({ start, end, display }) => {
          setStatusCardDate({
            dateRange: display,
            dateRangeStart: start,
            dateRangeEnd: end,
          });
          loadSrStatusChartRef.current?.({ fromDate: start, toDate: end });
        },
      }),
      initDashboardCardDateRangePicker(categoryCardDatePickerRef.current, {
        initialDisplay: categoryCardDate.dateRange,
        onApply: ({ start, end, display }) => {
          setCategoryCardDate({
            dateRange: display,
            dateRangeStart: start,
            dateRangeEnd: end,
          });
          loadSrCategoryChartRef.current?.({ fromDate: start, toDate: end });
        },
      }),
      initDashboardCardDateRangePicker(bvCardDatePickerRef.current, {
        initialDisplay: bvCardDate.dateRange,
        onApply: ({ start, end, display }) => {
          setBvCardDate({
            dateRange: display,
            dateRangeStart: start,
            dateRangeEnd: end,
          });
          loadProductVerticalChartRef.current?.({ fromDate: start, toDate: end });
        },
      }),
      initDashboardCardDateRangePicker(valueSlabCardDatePickerRef.current, {
        initialDisplay: valueSlabCardDate.dateRange,
        onApply: ({ start, end, display }) => {
          setValueSlabCardDate({
            dateRange: display,
            dateRangeStart: start,
            dateRangeEnd: end,
          });
          loadValueSlabChartRef.current?.({ fromDate: start, toDate: end });
        },
      }),
    ];
    return () => cleanups.forEach((fn) => fn?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-scope geo dropdowns when UserLocationMapping geography loads / updates
  useEffect(() => {
    if (!userGeography) return;
    loadCountryOptions();
    if (isExportCascade) {
      loadZoneOptions();
    } else if (filters.country?.length) {
      loadZoneOptions(filters.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGeography]);

  // Business Vertical scoped to selected Market
  useEffect(() => {
    if (!filters.market) {
      setBvOptions([]);
      return;
    }
    loadBVOptions(filters.market);
    setFilters((prev) => ({ ...prev, bv: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.market]);

  // Export: load all zones when market is Export
  useEffect(() => {
    if (!isExportCascade) return;
    loadZoneOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  // Domestic: load zones when country changes (All → keep scoped zone options)
  useEffect(() => {
    if (isExportCascade) return;
    if (isGeoAllFilterValue(filters.country)) {
      if (isUserGeoAllScopeForDashboard(loginUser, "country")) {
        loadZoneOptions();
        return;
      }
      const assignedCountries = getUserScopedGeoCodesForDashboard(
        loginUser,
        "country",
        userGeographyRef.current
      );
      const fromCountries = (Array.isArray(countryOptions) ? countryOptions : [])
        .map((o) => String(o?.id ?? "").trim())
        .filter((id) => id && !isGeoAllSelectionToken(id));
      const countryCodes =
        Array.isArray(assignedCountries) && assignedCountries.length
          ? assignedCountries.filter((c) => c && !isGeoAllSelectionToken(c))
          : fromCountries;
      if (countryCodes.length) {
        loadZoneOptions(countryCodes);
      } else {
        loadZoneOptions();
      }
      return;
    }
    if (filters.country?.length) {
      loadZoneOptions(filters.country);
    } else {
      setZoneOptions([]);
      setFilters((prev) => ({ ...prev, zone: [], region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade, filters.country]);

  // Clear geo when switching Export ↔ Domestic
  useEffect(() => {
    if (prevExportCascadeRef.current === null) {
      prevExportCascadeRef.current = isExportCascade;
      return;
    }
    if (prevExportCascadeRef.current === isExportCascade) return;
    prevExportCascadeRef.current = isExportCascade;

    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
    setFilters((prev) => ({
      ...prev,
      country: [],
      zone: [],
      region: [],
      territory: [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  // Refresh zone labels when country names load (Domestic: Zone (Country); Export: zone name only)
  useEffect(() => {
    if (!zoneOptions.length || !countryOptions.length) return;
    setZoneOptions((prev) =>
      prev.map((z) => ({
        ...z,
        text: formatZoneOptionLabel(z, countryOptions, {
          includeCountry: !isExportCascade,
        }),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, isExportCascade]);

  // Regions: require zone (+ country for Export). Skip reload when All is selected.
  useEffect(() => {
    if (isGeoAllFilterValue(filters.zone) || isGeoAllFilterValue(filters.country)) {
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (zOk && cOk) {
      loadRegionOptions(filters.zone, filters.country, zoneOptions, isExportCascade);
    } else {
      setRegionOptions([]);
      setFilters((prev) => ({ ...prev, region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.zone, filters.country, isExportCascade, zoneOptionIdsKey]);

  // Territories for selected region(s)
  useEffect(() => {
    if (
      isGeoAllFilterValue(filters.region) ||
      isGeoAllFilterValue(filters.zone) ||
      isGeoAllFilterValue(filters.country)
    ) {
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (!filters.region?.length) {
      setTerritoryOptions([]);
      setFilters((prev) => ({ ...prev, territory: [] }));
      return;
    }
    const regionsReady =
      regionOptions.length > 0 || regionOptionsRef.current.length > 0;
    if (!regionsReady) return;

    loadTerritoryOptions(
      filters.region,
      zoneOptions.length ? zoneOptions : zoneOptionsRef.current,
      regionOptions.length ? regionOptions : regionOptionsRef.current,
      filters.zone,
      filters.country
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.zone, filters.country, regionOptions, zoneOptionIdsKey]);

  // User scope ALL → select sentinel All (0000), same as reports
  useEffect(() => {
    if (!filters.market) return;
    if (isExportCascade) return;
    if (skipHeavyGeoHydration) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "zone")) return;
    if (!filters.country?.length || !zoneOptions.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.zone, allVal)) return prev;
      return { ...prev, zone: allVal, region: allVal, territory: allVal };
    });
  }, [loginUser, skipHeavyGeoHydration, zoneOptions, isExportCascade, filters.country, filters.market]);

  useEffect(() => {
    if (!filters.market) return;
    if (skipHeavyGeoHydration) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "country")) return;
    const list = isExportCascade ? exportCountryOptions : countryOptions;
    const parentOk = !isExportCascade || filters.zone?.length;
    if (!parentOk || !list.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.country, allVal)) return prev;
      if (isExportCascade) {
        return { ...prev, country: allVal, region: allVal, territory: allVal };
      }
      return {
        ...prev,
        country: allVal,
        zone: allVal,
        region: allVal,
        territory: allVal,
      };
    });
  }, [
    loginUser,
    skipHeavyGeoHydration,
    countryOptions,
    exportCountryOptions,
    isExportCascade,
    filters.zone,
    filters.market,
  ]);

  useEffect(() => {
    if (!filters.market) return;
    if (skipHeavyGeoHydration) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "region")) return;
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (!zOk || !cOk || !regionOptions.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.region, allVal)) return prev;
      return { ...prev, region: allVal, territory: allVal };
    });
  }, [
    loginUser,
    skipHeavyGeoHydration,
    regionOptions,
    isExportCascade,
    filters.zone,
    filters.country,
    filters.market,
  ]);

  useEffect(() => {
    if (!filters.market) return;
    if (skipHeavyGeoHydration) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "territory")) return;
    if (!filters.region?.length || !territoryOptions.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.territory, allVal)) return prev;
      return { ...prev, territory: allVal };
    });
  }, [loginUser, skipHeavyGeoHydration, territoryOptions, filters.region, filters.market]);

  const buildApproverDashboardParams = React.useCallback(
    (overrideParams = {}, dateOverride = null) => {
      let createdBy = null;
      const u = localStorage.getItem("user");
      if (u) {
        const user = JSON.parse(u);
        createdBy = user.userIdCode || user.UserIdCode || null;
      }
      const params = { createdBy };
      const fromDate =
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : dateOverride?.start !== undefined
            ? dateOverride.start
            : filters.dateRangeStart;
      const toDate =
        overrideParams.toDate !== undefined
          ? overrideParams.toDate
          : dateOverride?.end !== undefined
            ? dateOverride.end
            : filters.dateRangeEnd;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const statusList =
        overrideParams.status !== undefined ? overrideParams.status : filters.status;
      const bvList = overrideParams.bv !== undefined ? overrideParams.bv : filters.bv;
      const countryVal =
        overrideParams.country !== undefined ? overrideParams.country : filters.country;
      const zoneVal = overrideParams.zone !== undefined ? overrideParams.zone : filters.zone;
      const regionVal =
        overrideParams.region !== undefined ? overrideParams.region : filters.region;
      const territoryVal =
        overrideParams.territory !== undefined ? overrideParams.territory : filters.territory;
      const invoiceVal =
        overrideParams.invoiceNo !== undefined ? overrideParams.invoiceNo : filters.invoiceNo;
      const seNameVal =
        overrideParams.salesExecutiveName !== undefined
          ? overrideParams.salesExecutiveName
          : filters.salesExecutiveName;
      const complaintCatVal =
        overrideParams.complaintCategory !== undefined
          ? overrideParams.complaintCategory
          : filters.complaintCategory;

      if (invoiceVal && String(invoiceVal).trim()) {
        params.invoiceNumber = String(invoiceVal).trim();
      }
      if (Array.isArray(statusList) && statusList.length > 0) {
        const joinedStatus = statusList.filter(Boolean).join(",");
        if (joinedStatus) params.statusCode = joinedStatus;
      }
      if (Array.isArray(bvList) && bvList.length > 0) {
        const joinedBv = bvList.filter(Boolean).join(",");
        if (joinedBv) params.productVerticalCode = joinedBv;
      }
      const resolveGeoCsv = (selected) => {
        const fromUi = joinGeoFilterForApi(selected);
        if (fromUi) return fromUi;
        // Do not silently send assigned Country/Zone/Region/Territory.
        // Pending for My Approval is Next-Approver based (same as BCM list).
        // Auto-sending UserMaster.ZoneCode dropped SRs whose invoice territory is
        // blank (e.g. SR/FY27/D/0044 for U00000000985) so the card showed 0.
        return null;
      };

      const countryList = isExportCascade
        ? (() => {
            const codes = getCountryCodesForSelectedZones(
              Array.isArray(zoneVal) ? zoneVal : [],
              zoneOptionsRef.current
            );
            if (!codes.size) return countryOptions;
            return (countryOptions || []).filter(
              (c) => codes.has(c.id) || isGeoAllSelectionToken(c.id)
            );
          })()
        : countryOptions;

      const countryCsv = resolveGeoCsv(countryVal, "country", countryList);
      let zoneCsv = resolveGeoCsv(zoneVal, "zone", zoneOptionsRef.current);
      if (zoneCsv && isExportCascade) {
        const selectedIds = String(zoneCsv)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const expanded = expandZoneFilterCodes(selectedIds, zoneOptionsRef.current);
        zoneCsv = expanded.length ? expanded.join(",") : zoneCsv;
      }
      const regionCsv = resolveGeoCsv(regionVal, "region", regionOptionsRef.current);
      const territoryCsv = resolveGeoCsv(
        territoryVal,
        "territory",
        territoryOptions
      );
      if (countryCsv) params.countryCode = countryCsv;
      if (zoneCsv) params.zoneCode = zoneCsv;
      if (regionCsv) params.regionCode = regionCsv;
      if (territoryCsv) params.territoryCode = territoryCsv;
      if (seNameVal && seNameVal.trim()) {
        params.salesExecutiveName = seNameVal.trim();
      }
      const complaintCatTrim =
        complaintCatVal != null ? String(complaintCatVal).trim() : "";
      if (complaintCatTrim) params.complaintCategory = complaintCatTrim;
      return params;
    },
    [
      filters.dateRangeStart,
      filters.dateRangeEnd,
      filters.status,
      filters.bv,
      filters.country,
      filters.zone,
      filters.region,
      filters.territory,
      filters.invoiceNo,
      filters.salesExecutiveName,
      filters.complaintCategory,
      isExportCascade,
      countryOptions,
      territoryOptions,
      loginUser,
    ]
  );

  const fetchApproverDashboardPayload = React.useCallback(
    async (overrideParams = {}, dateOverride = null) => {
      try {
        dashboardAbortRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
      const ac = new AbortController();
      dashboardAbortRef.current = ac;
      try {
        const params = buildApproverDashboardParams(overrideParams, dateOverride);
        const response = await approverDashboardAPI.getApproverDashboardData(params, {
          signal: ac.signal,
        });
        return getApproverDashboardDataPayload(response);
      } catch (e) {
        if (
          e?.code === "ERR_CANCELED" ||
          e?.name === "CanceledError" ||
          e?.name === "AbortError"
        ) {
          return null;
        }
        console.error("Error loading approver dashboard data:", e);
        return null;
      }
    },
    [buildApproverDashboardParams]
  );

  const applySrCategoryFromPayload = (d) => {
    if (
      d?.srCategoryData &&
      Array.isArray(d.srCategoryData.labels) &&
      Array.isArray(d.srCategoryData.srCounts) &&
      d.srCategoryData.labels.length === d.srCategoryData.srCounts.length
    ) {
      setSrCategoryData({
        labels: d.srCategoryData.labels,
        srCounts: d.srCategoryData.srCounts,
      });
      return;
    }
    if (d?.complaintCategoryData) {
      setSrCategoryData({
        labels: ["Quality", "Dispatch", "Installation", "Commercial"],
        srCounts: [
          d.complaintCategoryData.quality || 0,
          d.complaintCategoryData.dispatch || 0,
          d.complaintCategoryData.installation || 0,
          d.complaintCategoryData.commercial || 0,
        ],
      });
      return;
    }
    setSrCategoryData({ labels: ["–"], srCounts: [0] });
  };

  const applyKpiFromPayload = (d) => {
    if (!d?.srData) return;
    setSrData({
      pendingSRCount: d.srData.pendingSRCount || 0,
      approvedSRCount: d.srData.approvedSRCount || 0,
      rejectedSRCount: d.srData.rejectedSRCount || 0,
      closedSRCount: d.srData.closedSRCount || 0,
      queryRaisedSRCount: d.srData.queryRaisedSRCount || 0,
      queryToRespondSRCount: d.srData.queryToRespondSRCount || 0,
      specialCaseSRCount: d.srData.specialCaseSRCount || 0,
      slaBreachedSRCount: d.srData.slaBreachedSRCount || 0,
      avgApprovalTatDays:
        d.srData.avgApprovalTatDays === null ||
        d.srData.avgApprovalTatDays === undefined ||
        d.srData.avgApprovalTatDays === ""
          ? null
          : d.srData.avgApprovalTatDays,
    });
  };

  const applyStatusChartFromPayload = (d) => {
    if (!d?.srData) return;
    setSrStatusChartData({
      pendingSRCount: d.srData.pendingSRCount || 0,
      approvedSRCount: d.srData.approvedSRCount || 0,
      rejectedSRCount: d.srData.rejectedSRCount || 0,
      closedSRCount: d.srData.closedSRCount || 0,
      queryRaisedSRCount: d.srData.queryRaisedSRCount || 0,
    });
  };

  const applyProductVerticalFromPayload = (d) => {
    const pv = d?.productVerticalData || d?.ProductVerticalData;
    if (!pv) return;
    const verticals = pv.verticals || pv.Verticals || [];
    const srCounts = pv.srCounts || pv.SrCounts || [];
    const rawCodes = pv.codes || pv.Codes || [];
    setProductVerticalData({
      codes: verticals.map((_, i) => {
        const c = rawCodes[i];
        return c != null && String(c).trim() ? String(c).trim() : "";
      }),
      verticals,
      srCounts,
    });
  };

  const applyValueSlabFromPayload = (d) => {
    if (!d?.valueSlabData) return;
    setValueSlabData({
      slabs: d.valueSlabData.slabs || [],
      srCounts: d.valueSlabData.srCounts || [],
    });
  };

  const applyFullDashboardPayload = (d) => {
    if (!d) return;
    applyKpiFromPayload(d);
    applyStatusChartFromPayload(d);
    applySrCategoryFromPayload(d);
    applyProductVerticalFromPayload(d);
    applyValueSlabFromPayload(d);
  };

  const runChartLoader = React.useCallback(async (widgetKey, task) => {
    setChartLoading((prev) => ({ ...prev, [widgetKey]: true }));
    try {
      await task();
    } finally {
      setChartLoading((prev) => ({ ...prev, [widgetKey]: false }));
    }
  }, []);

  /** One SP call feeds KPI cards + all charts when they share the same filters/dates. */
  const loadAllDashboardData = React.useCallback(
    async (overrideParams = {}) => {
      setChartLoading(createApproverWidgetLoadingState(true));
      try {
        const d = await fetchApproverDashboardPayload(overrideParams);
        applyFullDashboardPayload(d);
      } finally {
        setChartLoading(createApproverWidgetLoadingState(false));
      }
    },
    [fetchApproverDashboardPayload]
  );

  const loadSrStatusChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("status", async () => {
        const fromDate =
          overrideParams.fromDate !== undefined
            ? overrideParams.fromDate
            : statusCardDate.dateRangeStart;
        const toDate =
          overrideParams.toDate !== undefined ? overrideParams.toDate : statusCardDate.dateRangeEnd;
        const d = await fetchApproverDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        applyStatusChartFromPayload(d);
      });
    },
    [
      fetchApproverDashboardPayload,
      statusCardDate.dateRangeStart,
      statusCardDate.dateRangeEnd,
      runChartLoader,
    ]
  );

  const loadSrCategoryChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("category", async () => {
        const fromDate =
          overrideParams.fromDate !== undefined
            ? overrideParams.fromDate
            : categoryCardDate.dateRangeStart;
        const toDate =
          overrideParams.toDate !== undefined ? overrideParams.toDate : categoryCardDate.dateRangeEnd;
        const d = await fetchApproverDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) applySrCategoryFromPayload(d);
      });
    },
    [
      fetchApproverDashboardPayload,
      categoryCardDate.dateRangeStart,
      categoryCardDate.dateRangeEnd,
      runChartLoader,
    ]
  );

  const loadProductVerticalChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("bv", async () => {
        const fromDate =
          overrideParams.fromDate !== undefined ? overrideParams.fromDate : bvCardDate.dateRangeStart;
        const toDate =
          overrideParams.toDate !== undefined ? overrideParams.toDate : bvCardDate.dateRangeEnd;
        const d = await fetchApproverDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        applyProductVerticalFromPayload(d);
      });
    },
    [fetchApproverDashboardPayload, bvCardDate.dateRangeStart, bvCardDate.dateRangeEnd, runChartLoader]
  );

  const loadValueSlabChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("valueSlab", async () => {
        const fromDate =
          overrideParams.fromDate !== undefined
            ? overrideParams.fromDate
            : valueSlabCardDate.dateRangeStart;
        const toDate =
          overrideParams.toDate !== undefined
            ? overrideParams.toDate
            : valueSlabCardDate.dateRangeEnd;
        const d = await fetchApproverDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        applyValueSlabFromPayload(d);
      });
    },
    [
      fetchApproverDashboardPayload,
      valueSlabCardDate.dateRangeStart,
      valueSlabCardDate.dateRangeEnd,
      runChartLoader,
    ]
  );

  const syncChartDateFiltersFromMaster = React.useCallback(
    (master = null) => {
      const source = master ?? {
        dateRange: filters.dateRange,
        dateRangeStart: filters.dateRangeStart,
        dateRangeEnd: filters.dateRangeEnd,
      };
      const cardDate = {
        dateRange: source.dateRange || "",
        dateRangeStart: source.dateRangeStart || "",
        dateRangeEnd: source.dateRangeEnd || "",
      };
      setStatusCardDate(cardDate);
      setCategoryCardDate(cardDate);
      setBvCardDate(cardDate);
      setValueSlabCardDate(cardDate);
      chartCardDatePickerRefs.forEach((ref) => {
        if (ref.current) $(ref.current).val(cardDate.dateRange);
      });
      return cardDate;
    },
    [filters.dateRange, filters.dateRangeStart, filters.dateRangeEnd]
  );

  loadSrStatusChartRef.current = loadSrStatusChart;
  loadSrCategoryChartRef.current = loadSrCategoryChart;
  loadProductVerticalChartRef.current = loadProductVerticalChart;
  loadValueSlabChartRef.current = loadValueSlabChart;

  // Master filter date: sync chart pickers and reload count cards + all KPI charts
  useEffect(() => {
    if (skipDateFilterDashboardLoadRef.current) {
      skipDateFilterDashboardLoadRef.current = false;
      return;
    }
    if (filters.dateRangeStart || filters.dateRangeEnd) {
      syncChartDateFiltersFromMaster();
      loadAllDashboardData({
        fromDate: filters.dateRangeStart,
        toDate: filters.dateRangeEnd,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateRangeStart, filters.dateRangeEnd]);

  const getApproverPieExportRows = () => {
    const total = chartData.datasets[0].data.reduce((a, b) => a + b, 0);
    const rows = chartData.labels.map((label, index) => {
      const value = chartData.datasets[0].data[index];
      const percentage = total ? ((value / total) * 100).toFixed(2) : "0.00";
      return {
        Category: label,
        Count: value,
        Percentage: `${percentage}%`,
      };
    });
    rows.push({ Category: "Total", Count: total, Percentage: "100%" });
    return rows;
  };

  const handlePieChartExportExcel = async () => {
    await exportJsonToExcel(
      getApproverPieExportRows(),
      `sr-count-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by Status"
    );
  };
  const handlePieChartExportCsv = () => {
    exportRowsToCsv(
      getApproverPieExportRows(),
      `sr-count-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handlePieChartExportPdf = () => {
    exportRowsToPdf(
      getApproverPieExportRows(),
      `sr-count-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  // Initialize daterangepicker for top right
  useEffect(() => {
    if (dateRangePickerRef.current) {
      const $input = $(dateRangePickerRef.current);

      $input.daterangepicker({
        autoUpdateInput: false,
        locale: {
          cancelLabel: "Clear",
          applyLabel: "Apply",
          format: "DD-MM-YYYY",
        },
        opens: "left",
        drops: "down",
      });

      $input.on("apply.daterangepicker", function (ev, picker) {
        const start = picker.startDate.format("YYYY-MM-DD");
        const end = picker.endDate.format("YYYY-MM-DD");
        const displayText =
          picker.startDate.format("DD-MM-YYYY") +
          " - " +
          picker.endDate.format("DD-MM-YYYY");

        setStartDate(start);
        setEndDate(end);
        setDateRangeText(displayText);
        $(this).val(displayText);
      });

      $input.on("cancel.daterangepicker", function (ev, picker) {
        setStartDate("");
        setEndDate("");
        setDateRangeText("");
        $(this).val("");
      });

      // Cleanup
      return () => {
        if ($input.data("daterangepicker")) {
          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.data("daterangepicker").remove();
        }
      };
    }
  }, []);

  // Initialize filter date range picker with predefined ranges
  useEffect(() => {
    // Only initialize if filter panel is expanded and input exists
    if (!filterExpanded || !filterDateRangePickerRef.current) return;

    const $input = $(filterDateRangePickerRef.current);
    if (!$input.length) return;

    const initDateRangePicker = () => {
      // Destroy existing instance if any
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
      }

      try {
        $input.daterangepicker({
          autoUpdateInput: false,
          locale: {
            cancelLabel: "Clear",
            applyLabel: "Apply",
            format: "DD-MM-YYYY",
          },
          opens: "left",
          drops: "down",
          showDropdowns: true,
          ranges: getDashboardDateRangePresets(),
        });

        $input.on("apply.daterangepicker", function (ev, picker) {
          const displayText =
            picker.startDate.format("DD-MM-YYYY") +
            " - " +
            picker.endDate.format("DD-MM-YYYY");

          setFilters((prev) => ({
            ...prev,
            dateRange: displayText,
            dateRangeStart: picker.startDate.format("YYYY-MM-DD"),
            dateRangeEnd: picker.endDate.format("YYYY-MM-DD"),
          }));
          $(this).val(displayText);
        });

        $input.on("cancel.daterangepicker", function (ev, picker) {
          setFilters((prev) => ({ ...prev, dateRange: "" }));
          $(this).val("");
        });

        if (filters.dateRange) {
          $input.val(filters.dateRange);
        }
      } catch (error) {
        console.error("Error initializing daterangepicker:", error);
      }
    };

    // Initialize with a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initDateRangePicker();
    }, 100);

    // Reinitialize on click/focus if not working
    const handleInteraction = () => {
      if (!$input.data("daterangepicker")) {
        initDateRangePicker();
      }
    };

    $input.on("click.daterangepicker-reinit", handleInteraction);
    $input.on("focus.daterangepicker-reinit", handleInteraction);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if ($input.data("daterangepicker")) {
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
        $input.off("click.daterangepicker-reinit");
        $input.off("focus.daterangepicker-reinit");
        $input.data("daterangepicker").remove();
      }
    };
  }, [filterExpanded]);

  // Status is now handled by CustomMultiSelect; no Select2 initialization needed
  useEffect(() => {}, [filterExpanded]);

  // Initialize Select2 for SR Category (loaded from API)
  useEffect(() => {
    // Only initialize if filter panel is expanded and options are loaded
    if (!filterExpanded || !complaintCategorySelectRef.current || srCategoryOptions.length === 0)
      return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const $select = $(complaintCategorySelectRef.current);
      if (!$select.length) return;

      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      $select.empty();
      $select.append(new Option("Select SR Category", "", true, true));
      srCategoryOptions.forEach((option) => {
        $select.append(new Option(option.text, option.id, false, false));
      });

      $select.select2({
        width: "100%",
        placeholder: "Select SR Category",
        allowClear: true,
      });

      // Ensure arrow is visible
      setTimeout(() => {
        const $container = $select.next(".select2-container");
        if ($container.length) {
          const $arrow = $container.find(".select2-selection__arrow");
          if ($arrow.length === 0 || $arrow.find("b").length === 0) {
            $arrow.html('<b role="presentation"></b>');
          }
          $arrow.css({
            height: "42px",
            right: "12px",
            width: "20px",
            display: "block",
            position: "relative",
          });
          const $arrowB = $arrow.find("b");
          if ($arrowB.length) {
            $arrowB.css({
              borderColor: "#888 transparent transparent transparent",
              borderStyle: "solid",
              borderWidth: "5px 4px 0 4px",
              height: "0",
              left: "50%",
              marginLeft: "-4px",
              marginTop: "-2px",
              position: "absolute",
              top: "50%",
              width: "0",
              display: "block",
            });
          }
        }
      }, 100);

      // Update arrow direction when dropdown opens/closes
      $select.on("select2:open", function () {
        const $openArrow = $select
          .next(".select2-container")
          .find(".select2-selection__arrow b");
        if ($openArrow.length) {
          $openArrow.css({
            borderColor: "transparent transparent #888 transparent",
            borderWidth: "0 4px 5px 4px",
          });
        }
      });

      $select.on("select2:close", function () {
        const $closeArrow = $select
          .next(".select2-container")
          .find(".select2-selection__arrow b");
        if ($closeArrow.length) {
          $closeArrow.css({
            borderColor: "#888 transparent transparent transparent",
            borderWidth: "5px 4px 0 4px",
          });
        }
      });

      $select.on("change", function (e) {
        setFilters((prev) => ({
          ...prev,
          complaintCategory: e.target.value || "",
        }));
      });
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if (complaintCategorySelectRef.current) {
        const $select = $(complaintCategorySelectRef.current);
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      }
    };
  }, [filterExpanded]);

  // BV is handled by CustomMultiSelect; no Select2 initialization required here
  useEffect(() => {}, [filterExpanded, bvOptions]);

  const getApproverBarCategoryExportRows = () => {
    const rows = barChartData.labels.map((label, index) => ({
      Category: label,
      "SR Count": barChartData.datasets[0].data[index],
    }));
    const total = barChartData.datasets[0].data.reduce((a, b) => a + b, 0);
    rows.push({ Category: "Total", "SR Count": total });
    return rows;
  };

  const handleBarChartExportExcel = async () => {
    await exportJsonToExcel(
      getApproverBarCategoryExportRows(),
      `complaint-category-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by SR Category"
    );
  };
  const handleBarChartExportCsv = () => {
    exportRowsToCsv(
      getApproverBarCategoryExportRows(),
      `complaint-category-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleBarChartExportPdf = () => {
    exportRowsToPdf(
      getApproverBarCategoryExportRows(),
      `complaint-category-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "portrait", fontSize: 9 }
    );
  };

  const getProductVerticalExportRows = () => {
    const rows = productVerticalChartData.labels.map((label, index) => ({
      "Business Vertical": label,
      "SR Count": productVerticalChartData.datasets[0].data[index],
    }));
    const total = productVerticalChartData.datasets[0].data.reduce(
      (a, b) => a + b,
      0
    );
    rows.push({ "Business Vertical": "Total", "SR Count": total });
    return rows;
  };

  const handleProductVerticalExportExcel = async () => {
    await exportJsonToExcel(
      getProductVerticalExportRows(),
      `srs-by-product-vertical-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by Business Vertical"
    );
  };
  const handleProductVerticalExportCsv = () => {
    exportRowsToCsv(
      getProductVerticalExportRows(),
      `srs-by-product-vertical-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleProductVerticalExportPdf = () => {
    exportRowsToPdf(
      getProductVerticalExportRows(),
      `srs-by-product-vertical-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const getValueSlabExportRows = () => {
    const rows = valueSlabChartData.labels.map((label, index) => ({
      "Value Slab": label,
      "SR Count": valueSlabChartData.datasets[0].data[index],
    }));
    const total = valueSlabChartData.datasets[0].data.reduce(
      (a, b) => a + b,
      0
    );
    rows.push({ "Value Slab": "Total", "SR Count": total });
    return rows;
  };

  const handleValueSlabExportExcel = async () => {
    await exportJsonToExcel(
      getValueSlabExportRows(),
      `srs-by-value-slab-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by Value Slab"
    );
  };
  const handleValueSlabExportCsv = () => {
    exportRowsToCsv(
      getValueSlabExportRows(),
      `srs-by-value-slab-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleValueSlabExportPdf = () => {
    exportRowsToPdf(
      getValueSlabExportRows(),
      `srs-by-value-slab-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const renderChartLoaderOverlay = (widgetKey) =>
    chartLoading[widgetKey] ? (
      <div className="chart-widget-loading-overlay" aria-busy="true" aria-live="polite">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>
      </div>
    ) : null;

  const renderChartCardDateFilter = (pickerRef, displayText) => {
    const rangeLabel = (displayText || "").trim();
    return (
      <div className="chart-card-date-filter">
        <input
          type="text"
          ref={pickerRef}
          className="form-control form-control-light chart-card-date-input text-start"
          placeholder="Select date range"
          readOnly
          value={rangeLabel}
          style={{ width: `${getChartDateInputWidthPx(rangeLabel)}px` }}
        />
        <CalendarTodayIcon className="chart-card-date-icon" aria-hidden />
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      {/* <h1 className="dashboard-title">Approver Dashboard</h1> */}

      <div className="container-fluid">
        {/* Filter Card */}
        <div className="row mb-3">
          <div className="col-12">
            <div 
              className="card-panel mb-3 filter-panel"
              style={{ paddingBottom: filterExpanded ? "15px" : "2px" }}
            >
              <div
                className={`section-heading d-flex align-items-center gap-2 ${
                  filterExpanded ? "mb-3" : "filter-panel-heading-collapsed"
                }`}
                style={{
                  marginBottom: filterExpanded ? "14px" : 0,
                  cursor: "pointer",
                }}
                onClick={() => setFilterExpanded(!filterExpanded)}
              >
                <FilterListIcon
                  fontSize="small"
                  className="text-muted"
                  style={{
                    width: "18px",
                    height: "18px",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                />
                <span>Filter</span>
                <span className="filter-panel-active-daterange ms-auto">
                  {filters.dateRange || defaultFY.dateRange || "Select date range"}
                </span>
                {filterExpanded ? (
                  <ExpandLessIcon
                    fontSize="small"
                    className="text-muted"
                    style={{
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    className="text-muted"
                    style={{
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>

              {filterExpanded && (
              <div className="container-fluid">
                <div className="row g-3 align-items-end">
                  {/* Date Range */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Select Date Range
                    </label>
                    <div
                      className="input-icon"
                      style={{ position: "relative" }}
                    >
                      <input
                        type="text"
                        ref={filterDateRangePickerRef}
                        className="form-control form-control-light text-start"
                        placeholder="Select date range"
                        readOnly
                        style={{ height: "42px", textAlign: "left" }}
                      />
                      <CalendarTodayIcon
                        className="icon-right"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          width: "16px",
                          height: "16px",
                          color: "#6c757d",
                        }}
                      />
                    </div>
                  </div>

                  {/* Status Dropdown (Multiselect) */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Status
                    </label>
                    <select
                      ref={statusSelectRef}
                      className="form-select form-control-light filter-select text-start status-multiselect"
                      style={{ height: "42px", textAlign: "left", display: "none" }}
                      multiple
                    >
                      {statusOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                    <CustomMultiSelect
                      options={statusOptions}
                      value={filters.status || []}
                      onChange={(selected) =>
                        setFilters((prev) => ({
                          ...prev,
                          status: Array.isArray(selected) ? selected : [],
                        }))
                      }
                      placeholder="Select Status"
                      disabled={false}
                    />
                  </div>

                  {/* Complaint Category Dropdown */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      SR Category
                    </label>
                    <select
                      ref={complaintCategorySelectRef}
                      className="form-select form-control-light filter-select text-start"
                      style={{ height: "42px", textAlign: "left" }}
                    >
                      <option value="">Select SR Category</option>
                    </select>
                  </div>

                  {/* Product Vertical Dropdown */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Business Vertical
                    </label>
                    <select
                      ref={bvSelectRef}
                      className="form-select form-control-light filter-select text-start"
                      style={{ height: "42px", textAlign: "left", display: "none" }}
                      multiple
                    />
                    <CustomMultiSelect
                      options={bvOptions}
                      value={filters.bv || []}
                      onChange={(selected) =>
                        setFilters((prev) => ({
                          ...prev,
                          bv: Array.isArray(selected) ? selected : [],
                        }))
                      }
                      placeholder="Select Business Vertical"
                      disabled={false}
                    />
                  </div>

                  {/* Invoice No. Input — hidden per product request */}
                  <div className="col-12 col-md-6 col-lg-3 d-none">
                    <label className="input-label d-block mb-1 text-start">
                      SR Number / Invoice Number
                    </label>
                    <div
                      className="input-icon"
                      style={{ position: "relative" }}
                    >
                      <input
                        type="text"
                        className="form-control form-control-light text-start"
                        placeholder="Enter invoice number"
                        value={filters.invoiceNo}
                        onChange={handleInvoiceInputChange}
                        style={{ height: "42px", textAlign: "left" }}
                      />
                      <SearchIcon
                        className="icon-right"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          width: "16px",
                          height: "16px",
                          color: "#6c757d",
                        }}
                      />
                      {showInvoiceSuggestions &&
                        invoiceSuggestions.length > 0 && (
                          <ul
                            className="invoice-suggestions-list"
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              marginTop: "4px",
                              maxHeight: "200px",
                              overflowY: "auto",
                              zIndex: 1000,
                              listStyle: "none",
                              padding: 0,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          >
                            {invoiceSuggestions.map((suggestion, index) => (
                              <li
                                key={index}
                                onClick={() =>
                                  handleInvoiceSuggestionClick(suggestion)
                                }
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "#f5f5f5";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "white";
                                }}
                              >
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  </div>

                  {/* Sales Executive Name Input — hidden per product request */}
                  <div className="col-12 col-md-6 col-lg-3 d-none">
                    <label className="input-label d-block mb-1 text-start">
                      Sales Executive name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-light text-start"
                      placeholder="Enter sales executive name"
                      value={filters.salesExecutiveName}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          salesExecutiveName: e.target.value,
                        }))
                      }
                      style={{ height: "42px", textAlign: "left" }}
                    />
                  </div>

                  {/* Market Dropdown (scoped to user's assigned market(s)) */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="input-label d-block mb-1 text-start">
                      Market
                    </label>
                    <select
                      className="form-select form-control-light filter-select text-start"
                      value={filters.market || ""}
                      disabled={marketDropdownDisabled || !marketOptions.length}
                      onChange={(e) => {
                        const nextMarket = e.target.value || "";
                        setFilters((prev) => ({
                          ...prev,
                          market: nextMarket,
                          country: [],
                          zone: [],
                          region: [],
                          territory: [],
                          bv: [],
                        }));
                      }}
                      style={{ height: "42px", textAlign: "left" }}
                    >
                      <option value="">Select Market</option>
                      {marketOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.text}
                        </option>
                      ))}
                    </select>
                  </div>

                  {(isExportCascade
                    ? ["zone", "country", "region", "territory"]
                    : ["country", "zone", "region", "territory"]
                  ).map((geoKey) => {
                    const countryIsAll = isGeoAllFilterValue(filters.country);
                    const zoneIsAll = isGeoAllFilterValue(filters.zone);
                    const regionIsAll = isGeoAllFilterValue(filters.region);
                    if (geoKey === "country") {
                      const countryList = isExportCascade
                        ? exportCountryOptions
                        : countryOptions;
                      return (
                        <div key="country" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Country
                          </label>
                          <CustomMultiSelect
                            options={countryList}
                            value={filters.country || []}
                            onChange={(selected) =>
                              handleGeoFilterChange("country", selected, countryList)
                            }
                            placeholder={
                              isExportCascade
                                ? filters.zone?.length
                                  ? "Select Country"
                                  : "Select zone first"
                                : "Select Country"
                            }
                            disabled={
                              !filters.market ||
                              (isExportCascade &&
                                (!filters.zone?.length || zoneIsAll))
                            }
                            loading={geoLoading.country}
                            loadingText="Loading countries..."
                            showSelectAll={!countryIsAll && !zoneIsAll}
                          />
                        </div>
                      );
                    }
                    if (geoKey === "zone") {
                      return (
                        <div key="zone" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Zone
                          </label>
                          <CustomMultiSelect
                            options={zoneOptions}
                            value={filters.zone || []}
                            onChange={(selected) =>
                              handleGeoFilterChange("zone", selected, zoneOptions)
                            }
                            placeholder={
                              !filters.market
                                ? "Select market first"
                                : isExportCascade
                                  ? "Select zone(s)"
                                  : filters.country?.length
                                    ? "Select zone(s)"
                                    : "Select country first"
                            }
                            disabled={
                              !filters.market ||
                              (!isExportCascade &&
                                (!filters.country?.length || countryIsAll))
                            }
                            loading={geoLoading.zone}
                            loadingText="Loading zones..."
                            showSelectAll={!zoneIsAll && !countryIsAll}
                          />
                        </div>
                      );
                    }
                    if (geoKey === "region") {
                      const regionReady = isExportCascade
                        ? filters.zone?.length && filters.country?.length
                        : filters.zone?.length;
                      const lockedByAll = countryIsAll || zoneIsAll || regionIsAll;
                      return (
                        <div key="region" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Region
                          </label>
                          <CustomMultiSelect
                            options={regionOptions}
                            value={filters.region || []}
                            onChange={(selected) =>
                              handleGeoFilterChange("region", selected, regionOptions)
                            }
                            placeholder={
                              regionReady
                                ? "Select region(s)"
                                : isExportCascade
                                  ? "Select country first"
                                  : "Select zone first"
                            }
                            disabled={!filters.market || !regionReady || lockedByAll}
                            loading={geoLoading.region}
                            loadingText="Loading regions..."
                            showSelectAll={!lockedByAll}
                          />
                        </div>
                      );
                    }
                    const territoryLockedByAll =
                      countryIsAll || zoneIsAll || regionIsAll;
                    return (
                      <div key="territory" className="col-12 col-md-6 col-lg-3">
                        <label className="input-label d-block mb-1 text-start">
                          Territory
                        </label>
                        <CustomMultiSelect
                          options={territoryOptions}
                          value={filters.territory || []}
                          onChange={(selected) =>
                            handleGeoFilterChange(
                              "territory",
                              selected,
                              territoryOptions
                            )
                          }
                          placeholder={
                            filters.region?.length
                              ? "Select territory(ies)"
                              : "Select region first"
                          }
                          disabled={
                            !filters.market ||
                            !filters.region?.length ||
                            territoryLockedByAll
                          }
                          loading={geoLoading.territory}
                          loadingText="Loading territories..."
                          showSelectAll={!territoryLockedByAll}
                          selectAllLabel={
                            territoryOptions.length > 0
                              ? `Select all territories (${territoryOptions.length})`
                              : "Select all territories"
                          }
                        />
                      </div>
                    );
                  })}

                  {/* Action Buttons */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label
                      className="input-label d-block mb-1"
                      style={{ visibility: "hidden" }}
                    >
                      Actions
                    </label>
                    <div className="d-flex gap-2 filter-buttons">
                      <button
                        type="button"
                        className="btn btn-filter "
                        onClick={handleFilter}
                        style={{
                          background:
                            "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                          border: "none",
                          color: "white",
                          fontWeight: 600,
                          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                          borderRadius: "4px",
                          padding: "8px 24px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(102, 126, 234, 0.6)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(102, 126, 234, 0.4)";
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                      >
                        <FilterListIcon fontSize="small" className="me-1" />
                        Filter
                      </button>
                      <button
                        type="button"
                        className="btn btn-reset-filter"
                        onClick={handleReset}
                        title="Reset"
                        style={{
                          background:
                            "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                          border: "none",
                          color: "white",
                          fontWeight: 600,
                          boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                          borderRadius: "4px",
                          padding: "8px 16px",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(132, 250, 176, 0.6)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(132, 250, 176, 0.4)";
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                      >
                        <RestartAltIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* SR Count Cards Row — 4 per row (col-md-3) */}
        <div className="row dashboard-kpi-loader-wrap">
          {renderChartLoaderOverlay(APPROVER_KPI_WIDGET_KEY)}
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card pending"
              onClick={() =>
                navigateToBcmList({
                  statusToken: 2,
                  fromDate: statusCardDate.dateRangeStart || filters?.dateRangeStart,
                  toDate: statusCardDate.dateRangeEnd || filters?.dateRangeEnd,
                })
              }
            >
              <div className="sr-count-label">Pending for My Approval</div>
              <div className="sr-count-subtitle">
                SRs awaiting my approval
              </div>
              <div className="sr-count-value">{srData.pendingSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card rejected"
              onClick={() =>
                navigateToBcmList({
                  slaBreached: true,
                  fromDate: statusCardDate.dateRangeStart || filters?.dateRangeStart,
                  toDate: statusCardDate.dateRangeEnd || filters?.dateRangeEnd,
                })
              }
            >
              <div className="sr-count-label">SLA Breached SRs</div>
              <div className="sr-count-subtitle sla-breached-subtitle">
                SRs where TAT has been exceeded
              </div>
              <div className="sr-count-value">{srData.slaBreachedSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card special"
              onClick={() =>
                navigateToBcmList({
                  queryRaised: true,
                  fromDate: statusCardDate.dateRangeStart || filters?.dateRangeStart,
                  toDate: statusCardDate.dateRangeEnd || filters?.dateRangeEnd,
                })
              }
            >
              <div className="sr-count-label">Queries Raised by Me</div>
              <div className="sr-count-subtitle">
                Queries raised & awaiting responce
              </div>
              <div className="sr-count-value">{srData.queryRaisedSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card open"
              onClick={() =>
                navigateToBcmList({
                  queryToRespond: true,
                  fromDate: statusCardDate.dateRangeStart || filters?.dateRangeStart,
                  toDate: statusCardDate.dateRangeEnd || filters?.dateRangeEnd,
                })
              }
            >
              <div className="sr-count-label">Queries to be responded by Me</div>
              <div className="sr-count-subtitle">
              Queries I need to respond
              </div>
              <div className="sr-count-value">{srData.queryToRespondSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card open"
              onClick={() => navigateToBcmList({})}
            >
              <div className="sr-count-label">Avg Approval TAT (Days)</div>
              <div className="sr-count-subtitle">
                Average time taken for approval actions
              </div>
              <div className="sr-count-value">
                {(() => {
                  const tat = srData.avgApprovalTatDays;
                  if (tat === null || tat === undefined || tat === "") {
                    return "–";
                  }
                  // Whole days only (24h buckets), same display as admin Avg TAT
                  return String(Math.round(Number(tat)));
                })()}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card closed"
              onClick={() =>
                navigateToBcmList({
                  statusToken: isFinalCloser ? 5 : 3,
                  fromDate: statusCardDate.dateRangeStart || filters?.dateRangeStart,
                  toDate: statusCardDate.dateRangeEnd || filters?.dateRangeEnd,
                })
              }
            >
              <div className="sr-count-label">
                {isFinalCloser ? "Closed SRs" : "Approved SRs"}
              </div>
              <div className="sr-count-subtitle">
                {isFinalCloser
                  ? "SRs closed by the closer"
                  : "SRs approved by the approver"}
              </div>
              <div className="sr-count-value">
                {isFinalCloser ? srData.closedSRCount : srData.approvedSRCount}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card rejected"
              onClick={() => navigateToBcmList({ statusToken: 4 })}
            >
              <div className="sr-count-label">Rejected SRs</div>
              <div className="sr-count-subtitle">
                SRs rejected by the approver
              </div>
              <div className="sr-count-value">{srData.rejectedSRCount}</div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div
              className="sr-count-card special"
              onClick={() =>
                navigateToBcmList({
                  specialCase: true,
                  fromDate: statusCardDate.dateRangeStart || filters?.dateRangeStart,
                  toDate: statusCardDate.dateRangeEnd || filters?.dateRangeEnd,
                })
              }
            >
              <div className="sr-count-label">Special Case SRs</div>
              <div className="sr-count-subtitle">
                Rejected and Reopened SRs
              </div>
              <div className="sr-count-value">{srData.specialCaseSRCount}</div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="row charts-row mt-0">
          {/* Pie Chart Widget */}
          <div className="col-12 col-lg-6">
            <div className="pie-chart-widget">
              <div className="pie-chart-header">
                <h2 className="pie-chart-title">SRs by Status</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter(statusCardDatePickerRef, statusCardDate.dateRange)}
                  <DashboardExportDropdown
                    onExcel={handlePieChartExportExcel}
                    onPdf={handlePieChartExportPdf}
                    onCsv={handlePieChartExportCsv}
                  />
                </div>
              </div>

              <div className="pie-chart-container">
                {renderChartLoaderOverlay("status")}
                <div className="pie-chart-wrapper">
                  <Pie
                    ref={pieChartRef}
                    data={chartData}
                    options={chartOptions}
                  />
                </div>

                <div className="pie-chart-legend approver-status-legend">
                  <div className="legend-item">
                    <div className="legend-color pending"></div>
                    <span className="legend-label">Pending</span>
                    <span className="legend-value">{srStatusChartData.pendingSRCount}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color approved"></div>
                    <span className="legend-label">Approved</span>
                    <span className="legend-value">{srStatusChartData.approvedSRCount}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color rejected"></div>
                    <span className="legend-label rejected">Rejected</span>
                    <span className="legend-value">
                      {srStatusChartData.rejectedSRCount}
                    </span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color closed"></div>
                    <span className="legend-label">Closed</span>
                    <span className="legend-value">
                      {srStatusChartData.closedSRCount}
                    </span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color query"></div>
                    <span className="legend-label">Query raised</span>
                    <span className="legend-value">
                      {srStatusChartData.queryRaisedSRCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bar Chart Widget */}
          <div className="col-12 col-lg-6">
            <div className="bar-chart-widget">
              <div className="bar-chart-header">
                <h2 className="bar-chart-title">SRs by SR Category</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter(categoryCardDatePickerRef, categoryCardDate.dateRange)}
                  <DashboardExportDropdown
                    onExcel={handleBarChartExportExcel}
                    onPdf={handleBarChartExportPdf}
                    onCsv={handleBarChartExportCsv}
                  />
                </div>
              </div>

              <div className="bar-chart-wrapper">
                {renderChartLoaderOverlay("category")}
                <Bar
                  ref={barChartRef}
                  data={barChartData}
                  options={barChartOptions}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Product Vertical Chart Row — Top 10 Customers bar style (centered, full width) */}
        <div className="row charts-row">
          <div className="col-12">
            <div className="bar-chart-widget">
              <div className="bar-chart-header">
                <h2 className="bar-chart-title">SRs by Business Vertical</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter(bvCardDatePickerRef, bvCardDate.dateRange)}
                  <DashboardExportDropdown
                    onExcel={handleProductVerticalExportExcel}
                    onPdf={handleProductVerticalExportPdf}
                    onCsv={handleProductVerticalExportCsv}
                  />
                </div>
              </div>

              <div className="bar-chart-wrapper top-customers-chart-wrapper">
                {renderChartLoaderOverlay("bv")}
                <Bar
                  ref={productVerticalChartRef}
                  data={productVerticalChartData}
                  options={productVerticalChartOptions}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Value Slab Chart Row */}
        <div className="row charts-row">
          <div className="col-12">
            <div className="bar-chart-widget">
              <div className="bar-chart-header">
                <h2 className="bar-chart-title">
                  SRs by Value Slab
                </h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter(valueSlabCardDatePickerRef, valueSlabCardDate.dateRange)}
                  <DashboardExportDropdown
                    onExcel={handleValueSlabExportExcel}
                    onPdf={handleValueSlabExportPdf}
                    onCsv={handleValueSlabExportCsv}
                  />
                </div>
              </div>

              <div className="bar-chart-wrapper top-customers-chart-wrapper">
                {renderChartLoaderOverlay("valueSlab")}
                <Bar
                  ref={valueSlabChartRef}
                  data={valueSlabChartData}
                  options={valueSlabChartOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApproverDashboard;
